"use client"

import Link from "next/link"
import { ApertureStyles, T, MAXW, ArrowR } from "./_shared"
import { useAuth } from "@/lib/auth-context"

export default function ApertureFinalCta() {
  // This section's promise is action-based ("Drop a CSV ... we'll
  // hand it back cleaned"), NOT upgrade-based — so the CTA target
  // is whatever surface lets the visitor actually clean a list, not
  // an upgrade form. Earlier I mis-routed logged-in users to
  // /checkout?plan=pro, which made no sense: they came here for the
  // cleaning action, not to buy a plan.
  //   anon  → /try         (no-signup demo, cleans in the browser)
  //   user  → /dashboard   (their workspace, upload CSV from there)
  // Upgrade nudges live elsewhere on the site (header pill, usage
  // card, dataset-detail) — keep this section pure to its claim.
  const { user } = useAuth()
  const cta = user
    ? { href: "/dashboard", label: "Open your dashboard",                   labelShort: "Open dashboard" }
    : { href: "/try",       label: "Clean my list — free, no card needed", labelShort: "Clean my list — free" }
  return (
    <section className="relative overflow-hidden" style={{ background: T.bgInk, color: "#FAF7EE" }}>
      <ApertureStyles />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(800px 400px at 50% 0%, rgba(110,231,183,0.18), transparent 60%)`,
        }}
      />
      <div className={`relative ${MAXW} mx-auto px-6 lg:px-10 py-28 text-center`}>
        <h3
          className="leading-[0.92] tracking-[-0.045em] mx-auto max-w-[18ch]"
          style={{ fontSize: "clamp(2.6rem, 7vw, 5.6rem)", fontWeight: 800 }}
        >
          Send less.{" "}
          <em style={{ color: "#A8E0BF", fontStyle: "italic", fontWeight: 700 }}>Reach more.</em>
        </h3>
        <p
          className="mt-6 mx-auto max-w-[42ch]"
          style={{ fontSize: 16, color: "rgba(250,247,238,0.65)", lineHeight: 1.55 }}
        >
          Drop a CSV when you&rsquo;re ready. We&rsquo;ll hand it back cleaner, audited,
          and easier to import — free for your first 1,000 rows each month.
        </p>
        {/* Each block on its own line — wrapping divs force the
            stack. Previously the Link + <p> were both inline-flex
            siblings in a text-center parent, which made them sit on
            the same horizontal line and read as "button + caption
            mashed together". */}
        <div className="mt-10 px-4 sm:px-0">
          <Link
            href={cta.href}
            className="inline-flex items-center justify-center gap-2 transition-all hover:translate-x-[1px] w-full sm:w-auto max-w-full"
            style={{
              background: "#A8E0BF",
              color: T.accDeep,
              padding: "0.85rem 1.25rem",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              boxShadow: `0 24px 60px -20px rgba(110,231,183,0.4)`,
            }}
          >
            {/* Short label on mobile so the button doesn't span the
                full row width with an awkwardly long string; full
                label on sm+ where there's room for the second clause. */}
            <span className="sm:hidden">{cta.labelShort}</span>
            <span className="hidden sm:inline">{cta.label}</span>
            <ArrowR size={14} />
          </Link>
        </div>
        {/* Loss-aversion line — concrete cap + reset window so the
            "free" claim feels generous, not infinite (and invites
            action before the 1st of the next month). mt-7 instead of
            mt-5 so the line clears the button's soft drop-shadow
            (24px Y offset + 60px blur) without visually overlapping. */}
        <div className="mt-7">
          <p
            className="inline-flex items-center gap-2"
            style={{ fontSize: 12, color: "rgba(250,247,238,0.55)", fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.04em" }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#A8E0BF", boxShadow: "0 0 6px rgba(168,224,191,0.6)" }} />
            1,000 rows free each month · No credit card
          </p>
        </div>
        <p className="mt-3" style={{ fontSize: 11, color: "rgba(250,247,238,0.38)" }}>
          Keep the clean CSV, review the reasons, map the export when you need it
        </p>
      </div>
    </section>
  )
}
